/**
 * Outcome of one composer send. The admission record is evidence the patient-channel
 * path already returns and then discards; whisper is a PubNub publish with no admission
 * record. A JSON 401 from the ApiV2 fetch wrapper resolves `null` instead of throwing,
 * so `postLiveWebChatConversationEvent`'s destructure throws a TypeError that cannot be
 * told apart from a network failure — that path is therefore `unknown`, not `refused`.
 * Do not "fix" that in the fetch wrapper; it is a different change.
 */
export type SendResult =
    | { kind: 'admitted'; record: ApiV2.Amplify.PostLiveWebChatConversationEventRecord }
    | { kind: 'published'; record: null }
    | { kind: 'refused' }
    | { kind: 'unknown' }

/**
 * Classify a value thrown from the send path. Only a plain object (not an Error)
 * carrying a numeric `statusCode` / `code` / `status` in the 4xx range is `refused`:
 * a 4xx is the one shape that is evidence the message was NOT admitted. A 5xx is
 * `unknown` — the api can commit the ledger row and still fail the response — and
 * `refused` is the only kind that re-offers the card, so treating a 5xx as refused
 * would put a duplicate patient-visible send one click away. TypeError, SyntaxError,
 * a JSON body with only `message`/`errors`, and everything else — including the
 * 401-as-null TypeError — are `unknown`.
 */
export function classifySendFailure(err: unknown): 'refused' | 'unknown' {
    if (err instanceof Error) {
        return 'unknown'
    }
    if (err && typeof err === 'object') {
        const candidate = err as { statusCode?: unknown; code?: unknown; status?: unknown }
        const status =
            typeof candidate.statusCode === 'number'
                ? candidate.statusCode
                : typeof candidate.code === 'number'
                  ? candidate.code
                  : typeof candidate.status === 'number'
                    ? candidate.status
                    : undefined
        if (status !== undefined && status >= 400 && status <= 499) {
            return 'refused'
        }
    }
    return 'unknown'
}
